import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { ChevronLeft, X } from 'lucide-react';
import Portal from './Portal';
import MenuDropdown from './MenuDropdown';

const PET_MONTH_OPTIONS = [
  { value: '', label: 'Mês' },
  { value: 'Janeiro', label: 'Janeiro' },
  { value: 'Fevereiro', label: 'Fevereiro' },
  { value: 'Março', label: 'Março' },
  { value: 'Abril', label: 'Abril' },
  { value: 'Maio', label: 'Maio' },
  { value: 'Junho', label: 'Junho' },
  { value: 'Julho', label: 'Julho' },
  { value: 'Agosto', label: 'Agosto' },
  { value: 'Setembro', label: 'Setembro' },
  { value: 'Outubro', label: 'Outubro' },
  { value: 'Novembro', label: 'Novembro' },
  { value: 'Dezembro', label: 'Dezembro' }
];

const PET_SIZE_OPTIONS = [
  { value: '', label: 'Selecione o porte' },
  { value: 'Mini - até 4 kg', label: 'Mini - até 4 kg' },
  { value: 'Pequeno - entre 4 kg e 10 kg', label: 'Pequeno - entre 4 kg e 10 kg' },
  { value: 'Médio - entre 10 kg e 25 kg', label: 'Médio - entre 10 kg e 25 kg' },
  { value: 'Grande - entre 25 kg e 45 kg', label: 'Grande - entre 25 kg e 45 kg' },
  { value: 'Gigante - acima de 45 kg', label: 'Gigante - acima de 45 kg' }
];

const PET_COAT_OPTIONS = [
  { value: '', label: 'Selecione a pelagem' },
  { value: 'Curto', label: 'Curto' },
  { value: 'Médio', label: 'Médio' },
  { value: 'Longo', label: 'Longo' }
];

const PAYMENT_METHOD_OPTIONS = [
  { value: '', label: 'Selecione a forma de pagamento' },
  { value: 'PIX', label: 'PIX' },
  { value: 'Dinheiro', label: 'Dinheiro' },
  {
    value: 'Cartão (serão cobradas taxas adicionais)',
    label: 'Cartão (serão cobradas taxas adicionais)'
  }
];

const inputFieldClass = (hasError) =>
  `input-field !py-1.5 ${hasError ? 'border-red-500 dark:border-red-500 focus:ring-red-500' : ''}`;

const formatPhone = (value) => {
  if (!value) return '';
  const numbers = value.toString().replace(/\D/g, '');
  const limitedNumbers = numbers.slice(0, 11);

  if (limitedNumbers.length <= 10) {
    return limitedNumbers.replace(/(\d{2})(\d{4})(\d{0,4})/, (_match, p1, p2, p3) => {
      if (p3) return `(${p1}) ${p2}-${p3}`;
      if (p2) return `(${p1}) ${p2}`;
      if (p1) return `(${p1}`;
      return limitedNumbers;
    });
  }

  return limitedNumbers.replace(/(\d{2})(\d{5})(\d{0,4})/, (_match, p1, p2, p3) => {
    if (p3) return `(${p1}) ${p2}-${p3}`;
    if (p2) return `(${p1}) ${p2}`;
    if (p1) return `(${p1}`;
    return limitedNumbers;
  });
};

const INITIAL_WHATSAPP_FORM = {
  tutornome: '',
  tutorName: '',
  nomePet: '',
  petmesNascimento: '',
  petanoNascimento: '',
  petBirthMonth: '',
  petBirthYear: '',
  petporte: '',
  petpelagem: '',
  petSize: '',
  petCoat: '',
  raca: '',
  endereco: '',
  fonteContato: '',
  temAlergia: false,
  detalhesAlergia: '',
  temDorArticular: false,
  detalhesDorArticular: '',
  usaMedicamento: false,
  detalhesMedicamento: '',
  acompanhamentoVeterinario: false,
  detalhesAcompanhamento: '',
  authorizeImidade: false,
  autorizaImagem: false,
  telefone: '',
  service: '',
  tipoServico: '',
  servicoLevaTraz: false,
  paymentMethod: '',
  observations: ''
};

const WhatsAppModal = ({ isOpen, onClose, businessPhone }) => {
  const sheetRef = useRef(null);
  const observationsRef = useRef(null);
  const [bookingStep, setBookingStep] = useState('choose');
  const [whatsappFieldErrors, setWhatsappFieldErrors] = useState({});
  const [whatsappForm, setWhatsappForm] = useState(INITIAL_WHATSAPP_FORM);

  const handleCloseWhatsAppModal = () => {
    setWhatsappForm(INITIAL_WHATSAPP_FORM);
    setWhatsappFieldErrors({});
    setBookingStep('choose');
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setWhatsappFieldErrors({});
    }
    setBookingStep('choose');
  }, [isOpen]);

  const handleGoBack = () => {
    setBookingStep('choose');
    setWhatsappFieldErrors({});
  };

  const clearWhatsappFieldErrors = useCallback((keys) => {
    if (!keys.length) return;
    setWhatsappFieldErrors((prev) => {
      const next = { ...prev };
      keys.forEach((k) => {
        if (k) delete next[k];
      });
      return next;
    });
  }, []);

  const handleWhatsAppFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    const lettersOnlyFields = ['tutorName', 'nomePet', 'raca'];
    const numbersOnlyFields = ['telefone', 'phone'];
    
    // Mapeamento de nomes de checkboxes para campos do estado
    const checkboxMapping = {
      'hasAllergy': 'temAlergia',
      'hasJointPain': 'temDorArticular',
      'usesMedication': 'usaMedicamento',
      'vetFollowUp': 'acompanhamentoVeterinario',
      'authorizeImage': 'autorizaImagem'
    };
    
    // Mapeamento de nomes de campos de detalhes para campos do estado
    const detailsFieldMapping = {
      'allergyDetails': 'detalhesAlergia',
      'jointPainDetails': 'detalhesDorArticular',
      'medicationDetails': 'detalhesMedicamento',
      'vetFollowUpDetails': 'detalhesAcompanhamento'
    };
    
    // Mapeamento geral de nomes de campos para campos do estado
    const fieldMapping = {
      'phone': 'telefone',
      'address': 'endereco',
      'contactSource': 'fonteContato',
      'breed': 'raca'
    };
    
    let processedValue = value;
    
    if (type === 'checkbox') {
      const stateField = checkboxMapping[name] || name;
      setWhatsappForm(prev => ({
        ...prev,
        [stateField]: checked,
        ...(stateField === 'temAlergia' && !checked ? { detalhesAlergia: '' } : {}),
        ...(stateField === 'temDorArticular' && !checked ? { detalhesDorArticular: '' } : {}),
        ...(stateField === 'usaMedicamento' && !checked ? { detalhesMedicamento: '' } : {}),
        ...(stateField === 'acompanhamentoVeterinario' && !checked ? { detalhesAcompanhamento: '' } : {})
      }));
      const keysToClear = [stateField];
      if (stateField === 'temAlergia' && !checked) keysToClear.push('detalhesAlergia');
      if (stateField === 'temDorArticular' && !checked) keysToClear.push('detalhesDorArticular');
      if (stateField === 'usaMedicamento' && !checked) keysToClear.push('detalhesMedicamento');
      if (stateField === 'acompanhamentoVeterinario' && !checked) keysToClear.push('detalhesAcompanhamento');
      clearWhatsappFieldErrors(keysToClear);
      return;
    }
    
    if (lettersOnlyFields.includes(name)) {
      processedValue = value.replace(/[^a-zA-ZáàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ\s]/g, '');
    }
    
    if (numbersOnlyFields.includes(name)) {
      processedValue = formatPhone(value);
    }
    
    // Aplicar mapeamento de campos se necessário
    const stateField = detailsFieldMapping[name] || fieldMapping[name] || name;
    
    setWhatsappForm(prev => ({
      ...prev,
      [stateField]: processedValue
    }));
    clearWhatsappFieldErrors([detailsFieldMapping[name] || fieldMapping[name] || name]);
  };

  const handleBirthDateUnknownChange = (e) => {
    if (e.target.checked) {
      setWhatsappForm(prev => ({
        ...prev,
        petmesNascimento: '',
        petanoNascimento: '',
        petBirthMonth: '',
        petBirthYear: ''
      }));
      clearWhatsappFieldErrors(['petBirthMonth', 'petBirthYear']);
    }
  };

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();

    const isReturningClient = bookingStep === 'returning';
    const errors = {};
    if (!whatsappForm.tutorName?.trim()) errors.tutorName = true;
    const phoneDigits = String(whatsappForm.telefone || '').replace(/\D/g, '');
    if (!phoneDigits || phoneDigits.length < 10) errors.telefone = true;
    if (!whatsappForm.nomePet?.trim()) errors.nomePet = true;
    if (!whatsappForm.service?.trim()) errors.service = true;

    if (!isReturningClient) {
      if (!whatsappForm.endereco?.trim()) errors.endereco = true;
      if (!whatsappForm.raca?.trim()) errors.raca = true;
      if (!whatsappForm.petSize?.trim()) errors.petSize = true;
      if (!whatsappForm.petCoat?.trim()) errors.petCoat = true;
      if (!whatsappForm.paymentMethod?.trim()) errors.paymentMethod = true;
      if (whatsappForm.temAlergia && !whatsappForm.detalhesAlergia?.trim()) errors.detalhesAlergia = true;
      if (whatsappForm.temDorArticular && !whatsappForm.detalhesDorArticular?.trim()) errors.detalhesDorArticular = true;
      if (whatsappForm.usaMedicamento && !whatsappForm.detalhesMedicamento?.trim()) errors.detalhesMedicamento = true;
      if (whatsappForm.acompanhamentoVeterinario && !whatsappForm.detalhesAcompanhamento?.trim()) {
        errors.detalhesAcompanhamento = true;
      }
    }

    if (Object.keys(errors).length > 0) {
      setWhatsappFieldErrors(errors);
      return;
    }
    setWhatsappFieldErrors({});

    let message;

    if (isReturningClient) {
      message = `Olá! Gostaria de agendar um serviço para meu pet.

*Tipo de Cliente:*
Já sou cliente

*Informações do Tutor:*
Nome: ${whatsappForm.tutorName}
Telefone: ${whatsappForm.telefone}

*Informações do Pet:*
Nome: ${whatsappForm.nomePet}

*Serviço Desejado:*
${whatsappForm.service}`;
    } else {
      const healthInfo = [];

      if (whatsappForm.temAlergia) {
        healthInfo.push(`✓ Tem alergia${whatsappForm.detalhesAlergia ? `: ${whatsappForm.detalhesAlergia}` : ''}`);
      } else {
        healthInfo.push('✗ Não possui alergias conhecidas');
      }

      if (whatsappForm.temDorArticular) {
        healthInfo.push(`✓ Tem dor articular${whatsappForm.detalhesDorArticular ? `: ${whatsappForm.detalhesDorArticular}` : ''}`);
      } else {
        healthInfo.push('✗ Não possui dores articulares');
      }

      if (whatsappForm.usaMedicamento) {
        healthInfo.push(`✓ Usa medicamento${whatsappForm.detalhesMedicamento ? `: ${whatsappForm.detalhesMedicamento}` : ''}`);
      } else {
        healthInfo.push('✗ Não usa medicamentos');
      }

      if (whatsappForm.acompanhamentoVeterinario) {
        healthInfo.push(`✓ Está fazendo acompanhamento veterinário${whatsappForm.detalhesAcompanhamento ? `: ${whatsappForm.detalhesAcompanhamento}` : ''}`);
      } else {
        healthInfo.push('✗ Não está fazendo acompanhamento veterinário');
      }

      message = `Olá! Gostaria de agendar um serviço para meu pet.

*Tipo de Cliente:*
Primeiro agendamento ArukDog

*Informações do Tutor:*
Nome: ${whatsappForm.tutorName}
Telefone: ${whatsappForm.telefone}
Endereço: ${whatsappForm.endereco}
${whatsappForm.fonteContato ? `Como nos conheceu: ${whatsappForm.fonteContato}` : ''}

*Informações do Pet:*
Nome: ${whatsappForm.nomePet}
Raça: ${whatsappForm.raca}
Data de Nascimento Aproximada: ${whatsappForm.petBirthMonth && whatsappForm.petBirthYear ? `${whatsappForm.petBirthMonth}/${whatsappForm.petBirthYear}` : 'Não informado'}
Porte: ${whatsappForm.petSize}
Pelagem: ${whatsappForm.petCoat}

*Informações de Saúde:*
${healthInfo.join('\n')}

*Serviço Desejado:*
${whatsappForm.service}
Serviço de leva e traz: ${whatsappForm.servicoLevaTraz ? 'Sim' : 'Não'}
           
*Forma de Pagamento:*
${whatsappForm.paymentMethod}

${whatsappForm.autorizaImagem ? '✓ Autorizo o uso de imagem do pet nas redes sociais' : '✗ Não autorizo o uso de imagem do pet nas redes sociais'}

${whatsappForm.observations ? `*Observações:*\n${whatsappForm.observations}` : ''}`;
    }

    const phoneNumber = businessPhone;
    if (!phoneNumber) {
      return;
    }
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    handleCloseWhatsAppModal();
  };

  // Travar scroll do fundo quando o modal estiver aberto (mesma solução do modal de agendamento)
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      const prevHtmlOverflow = document.documentElement.style.overflow;
      const prevBodyOverflow = document.body.style.overflow;
      const prevBodyPosition = document.body.style.position;
      const prevBodyTop = document.body.style.top;
      const prevBodyWidth = document.body.style.width;
      const prevBodyHeight = document.body.style.height;

      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';

      return () => {
        document.documentElement.style.overflow = prevHtmlOverflow;
        document.body.style.overflow = prevBodyOverflow;
        document.body.style.position = prevBodyPosition;
        document.body.style.top = prevBodyTop;
        document.body.style.width = prevBodyWidth;
        document.body.style.height = prevBodyHeight;
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const petYearOptions = useMemo(
    () => [
      { value: '', label: 'Ano' },
      ...Array.from({ length: 20 }, (_, i) => {
        const y = new Date().getFullYear() - i;
        return { value: String(y), label: String(y) };
      })
    ],
    []
  );

  const fitObservationsTextarea = useCallback(() => {
    const el = observationsRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    fitObservationsTextarea();
    const onResize = () => fitObservationsTextarea();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen, whatsappForm.observations, fitObservationsTextarea]);

  if (!isOpen) return null;

  return (
    <Portal>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center sm:items-center z-[9999] p-0 sm:p-4"
        onClick={handleCloseWhatsAppModal}
        data-modal="whatsapp"
      >
        <div 
          ref={sheetRef}
          className="relative flex h-dvh max-h-dvh min-h-0 w-full flex-col overflow-hidden bg-white sm:h-auto sm:min-h-0 sm:max-h-[90vh] sm:max-w-md rounded-none sm:rounded-lg"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Cabeçalho acima dos menus portaled (z-50 > z-40 do MenuDropdown) */}
          <div className="relative z-50 flex shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
            <div className="flex w-10 shrink-0 justify-start">
              {bookingStep !== 'choose' && (
                <button
                  type="button"
                  onClick={handleGoBack}
                  className="flex items-center gap-1 p-1 text-sm font-medium text-[#5CC1B3] transition-colors hover:text-[#4aad9f]"
                  aria-label="Voltar"
                >
                  <ChevronLeft className="h-5 w-5" />
                  <span className="hidden sm:inline">Voltar</span>
                </button>
              )}
            </div>
            <h2 className="min-w-0 flex-1 text-center text-lg font-bold text-gray-900 sm:text-xl">
              {bookingStep === 'first' ? 'Agendar Primeiro Serviço' : 'Agendar Serviço'}
            </h2>
            <div className="flex w-10 shrink-0 justify-end">
              <button
                onClick={handleCloseWhatsAppModal}
                className="p-1 text-gray-400 transition-colors hover:text-gray-600"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {bookingStep === 'choose' ? (
            <div
              className="flex min-h-0 flex-1 flex-col justify-center overflow-y-auto p-6 sm:justify-start"
              style={{ overscrollBehavior: 'contain' }}
            >
              <div className="w-full space-y-5">
                <button
                  type="button"
                  onClick={() => setBookingStep('first')}
                  className="w-full rounded-lg border-2 border-gray-300 px-4 py-4 text-center font-semibold text-gray-900 transition-colors hover:border-[#5CC1B3] hover:bg-gray-50"
                >
                  Primeiro agendamento ArukDog!
                </button>
                <button
                  type="button"
                  onClick={() => setBookingStep('returning')}
                  className="w-full rounded-lg border-2 border-gray-300 px-4 py-4 text-center font-semibold text-gray-900 transition-colors hover:border-[#5CC1B3] hover:bg-gray-50"
                >
                  Já sou cliente!
                </button>
              </div>
            </div>
          ) : (
          <form
            onSubmit={handleWhatsAppSubmit}
            className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6"
            style={{ overscrollBehavior: 'contain' }}
            noValidate
          >
            <div>
              <span id="tutorName-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                Nome do Tutor
              </span>
              <input
                type="text"
                id="tutorName"
                name="tutorName"
                value={whatsappForm.tutorName}
                onChange={handleWhatsAppFormChange}
                aria-labelledby="tutorName-caption"
                className={inputFieldClass(whatsappFieldErrors.tutorName)}
                placeholder="Digite seu nome"
              />
              {whatsappFieldErrors.tutorName && (
                <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
              )}
            </div>

            <div>
              <span id="phone-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                Telefone
              </span>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={whatsappForm.telefone}
                onChange={handleWhatsAppFormChange}
                aria-labelledby="phone-caption"
                className={inputFieldClass(whatsappFieldErrors.telefone)}
                placeholder="(41) 99999-9999"
              />
              {whatsappFieldErrors.telefone && (
                <span className="mt-1 block text-xs text-red-500">Preencha com um telefone válido (mín. 10 dígitos)</span>
              )}
            </div>

            {bookingStep === 'first' && (
            <div>
              <span id="address-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                Endereço
              </span>
              <input
                type="text"
                id="address"
                name="address"
                value={whatsappForm.endereco}
                onChange={handleWhatsAppFormChange}
                aria-labelledby="address-caption"
                className={inputFieldClass(whatsappFieldErrors.endereco)}
                placeholder="Rua, número, bairro"
              />
              {whatsappFieldErrors.endereco && (
                <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
              )}
            </div>
            )}

            {bookingStep === 'first' && (
            <div>
              <span id="contactSource-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                Como nos conheceu?
              </span>
              <input
                type="text"
                id="contactSource"
                name="contactSource"
                value={whatsappForm.fonteContato}
                onChange={handleWhatsAppFormChange}
                aria-labelledby="contactSource-caption"
                className="input-field !py-1.5"
                placeholder="Ex: Instagram, indicação, etc."
              />
            </div>
            )}

            <div>
              <span id="nomePet-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                Nome do Pet
              </span>
              <input
                type="text"
                id="nomePet"
                name="nomePet"
                value={whatsappForm.nomePet}
                onChange={handleWhatsAppFormChange}
                aria-labelledby="nomePet-caption"
                className={inputFieldClass(whatsappFieldErrors.nomePet)}
                placeholder="Digite o nome do pet"
              />
              {whatsappFieldErrors.nomePet && (
                <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
              )}
            </div>

            {bookingStep === 'first' && (
            <>
            <div>
              <span id="breed-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                Raça do Pet
              </span>
              <input
                type="text"
                id="breed"
                name="breed"
                value={whatsappForm.raca}
                onChange={handleWhatsAppFormChange}
                aria-labelledby="breed-caption"
                className={inputFieldClass(whatsappFieldErrors.raca)}
                placeholder="Ex: Golden Retriever"
              />
              {whatsappFieldErrors.raca && (
                <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
              )}
            </div>

            <div>
              <span id="petBirthDate-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                Data de nascimento do Pet (aproximadamente)
              </span>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <MenuDropdown
                    id="petBirthMonth"
                    name="petBirthMonth"
                    value={whatsappForm.petBirthMonth}
                    onChange={handleWhatsAppFormChange}
                    options={PET_MONTH_OPTIONS}
                    placeholder="Mês"
                    portalRootRef={sheetRef}
                    ariaLabelledBy="petBirthDate-caption"
                  />
                </div>
                <div>
                  <MenuDropdown
                    id="petBirthYear"
                    name="petBirthYear"
                    value={whatsappForm.petBirthYear}
                    onChange={handleWhatsAppFormChange}
                    options={petYearOptions}
                    placeholder="Ano"
                    portalRootRef={sheetRef}
                    ariaLabelledBy="petBirthDate-caption"
                  />
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  onChange={handleBirthDateUnknownChange}
                  aria-label="Não sei informar a data de nascimento do pet"
                  className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                />
                <span className="cursor-default text-sm text-muted-light dark:text-muted-dark">Não sei informar</span>
              </div>
            </div>

            <div>
              <span id="petSize-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                Porte do Pet
              </span>
              <MenuDropdown
                id="petSize"
                name="petSize"
                value={whatsappForm.petSize}
                onChange={handleWhatsAppFormChange}
                options={PET_SIZE_OPTIONS}
                placeholder="Selecione o porte"
                portalRootRef={sheetRef}
                ariaLabelledBy="petSize-caption"
                className={whatsappFieldErrors.petSize ? '!border-red-500 dark:!border-red-500' : ''}
              />
              {whatsappFieldErrors.petSize && (
                <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
              )}
            </div>

            <div>
              <span id="petCoat-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                Comprimento da pelagem do Pet
              </span>
              <MenuDropdown
                id="petCoat"
                name="petCoat"
                value={whatsappForm.petCoat}
                onChange={handleWhatsAppFormChange}
                options={PET_COAT_OPTIONS}
                placeholder="Selecione a pelagem"
                portalRootRef={sheetRef}
                ariaLabelledBy="petCoat-caption"
                className={whatsappFieldErrors.petCoat ? '!border-red-500 dark:!border-red-500' : ''}
              />
              {whatsappFieldErrors.petCoat && (
                <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
              )}
            </div>
            </>
            )}

            <div>
              <span id="service-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                Serviço Desejado
              </span>
              <input
                type="text"
                id="service"
                name="service"
                value={whatsappForm.service}
                onChange={handleWhatsAppFormChange}
                aria-labelledby="service-caption"
                className={inputFieldClass(whatsappFieldErrors.service)}
                placeholder="Ex: Banho, Tosa, Banho + Tosa"
              />
              {whatsappFieldErrors.service && (
                <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
              )}
            </div>

            {bookingStep === 'first' && (
            <>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="servicoLevaTraz"
                  checked={whatsappForm.servicoLevaTraz}
                  onChange={handleWhatsAppFormChange}
                  aria-label="Desejo serviço de leva e traz do pet"
                  className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="cursor-default text-sm text-foreground-light dark:text-foreground-dark">
                  Desejo serviço de leva e traz do pet
                </span>
              </div>
            </div>

            <div>
              <span id="paymentMethod-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                Forma de Pagamento
              </span>
              <MenuDropdown
                id="paymentMethod"
                name="paymentMethod"
                value={whatsappForm.paymentMethod}
                onChange={handleWhatsAppFormChange}
                options={PAYMENT_METHOD_OPTIONS}
                placeholder="Selecione a forma de pagamento"
                portalRootRef={sheetRef}
                ariaLabelledBy="paymentMethod-caption"
                className={whatsappFieldErrors.paymentMethod ? '!border-red-500 dark:!border-red-500' : ''}
              />
              {whatsappFieldErrors.paymentMethod && (
                <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="hasAllergy"
                  checked={whatsappForm.temAlergia}
                  onChange={handleWhatsAppFormChange}
                  aria-label="Tem alergia"
                  className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="cursor-default text-sm text-foreground-light dark:text-foreground-dark">Tem alergia</span>
              </div>
              {whatsappForm.temAlergia && (
                <>
                  <input
                    type="text"
                    name="allergyDetails"
                    value={whatsappForm.detalhesAlergia}
                    onChange={handleWhatsAppFormChange}
                    className={`input-field !py-1.5 text-sm ${whatsappFieldErrors.detalhesAlergia ? '!border-red-500 dark:!border-red-500' : ''}`}
                    placeholder="Descreva a alergia específica..."
                  />
                  {whatsappFieldErrors.detalhesAlergia && (
                    <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
                  )}
                </>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="hasJointPain"
                  checked={whatsappForm.temDorArticular}
                  onChange={handleWhatsAppFormChange}
                  aria-label="Tem dor articular"
                  className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="cursor-default text-sm text-foreground-light dark:text-foreground-dark">Tem dor articular</span>
              </div>
              {whatsappForm.temDorArticular && (
                <>
                  <input
                    type="text"
                    name="jointPainDetails"
                    value={whatsappForm.detalhesDorArticular}
                    onChange={handleWhatsAppFormChange}
                    className={`input-field !py-1.5 text-sm ${whatsappFieldErrors.detalhesDorArticular ? '!border-red-500 dark:!border-red-500' : ''}`}
                    placeholder="Descreva a dor articular..."
                  />
                  {whatsappFieldErrors.detalhesDorArticular && (
                    <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
                  )}
                </>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="usesMedication"
                  checked={whatsappForm.usaMedicamento}
                  onChange={handleWhatsAppFormChange}
                  aria-label="Usa medicamento"
                  className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="cursor-default text-sm text-foreground-light dark:text-foreground-dark">Usa medicamento</span>
              </div>
              {whatsappForm.usaMedicamento && (
                <>
                  <input
                    type="text"
                    name="medicationDetails"
                    value={whatsappForm.detalhesMedicamento}
                    onChange={handleWhatsAppFormChange}
                    className={`input-field !py-1.5 text-sm ${whatsappFieldErrors.detalhesMedicamento ? '!border-red-500 dark:!border-red-500' : ''}`}
                    placeholder="Descreva o medicamento usado..."
                  />
                  {whatsappFieldErrors.detalhesMedicamento && (
                    <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
                  )}
                </>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="vetFollowUp"
                  checked={whatsappForm.acompanhamentoVeterinario}
                  onChange={handleWhatsAppFormChange}
                  aria-label="Está fazendo acompanhamento veterinário"
                  className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="cursor-default text-sm text-foreground-light dark:text-foreground-dark">Está fazendo acompanhamento veterinário</span>
              </div>
              {whatsappForm.acompanhamentoVeterinario && (
                <>
                  <input
                    type="text"
                    name="vetFollowUpDetails"
                    value={whatsappForm.detalhesAcompanhamento}
                    onChange={handleWhatsAppFormChange}
                    className={`input-field !py-1.5 text-sm ${whatsappFieldErrors.detalhesAcompanhamento ? '!border-red-500 dark:!border-red-500' : ''}`}
                    placeholder="Descreva o acompanhamento..."
                  />
                  {whatsappFieldErrors.detalhesAcompanhamento && (
                    <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
                  )}
                </>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="authorizeImage"
                  checked={whatsappForm.autorizaImagem}
                  onChange={handleWhatsAppFormChange}
                  aria-label="Autorizo o uso de imagem do pet nas redes sociais"
                  className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="cursor-default text-sm text-foreground-light dark:text-foreground-dark">
                  Autorizo o uso de imagem do pet nas redes sociais
                </span>
              </div>
            </div>

            <div>
              <span id="observations-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                Observações (opcional)
              </span>
              <textarea
                ref={observationsRef}
                id="observations"
                name="observations"
                value={whatsappForm.observations}
                onChange={handleWhatsAppFormChange}
                rows={1}
                aria-labelledby="observations-caption"
                className="input-field min-h-[4.5rem] w-full resize-none overflow-hidden !py-1.5"
                placeholder="Alguma observação adicional..."
              />
            </div>
            </>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleCloseWhatsAppModal}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                style={{ backgroundColor: '#5CC1B3' }}
              >
                Enviar por WhatsApp
              </button>
            </div>
          </form>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default WhatsAppModal;
